/*
Purpose: Calendar (v2) — swimlane day view, week grid and the four request inboxes.
Project: TCMS

This screen reads and writes the same `api_index.php` endpoints as the original
calendar; it is an alternative presentation, not a second source of truth. The heavy
lifting is split out so this file stays a container:

  - `calendar-v2/calendar-api.js`      request contracts
  - `calendar-v2/session-model.js`     raw rows to view model, filtering, aggregates
  - `calendar-v2/useCalendarData.js`   reads and refetching
  - `calendar-v2/useCalendarActions.js` writes, toasts and busy state
  - `calendar-v2/components/*`         presentation
*/

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import { getUserSetting } from './UserSetting';
import {
  CALENDAR_VIEW,
  DEFAULT_HOUR_WINDOW,
  DENSITY,
  DAY_LAYOUT,
  FORFEIT_TYPE,
  HOURS_FILTER,
  PAUSE_APPLY_TYPE,
  QUICK_FILTER,
  DIAGNOSTICS_LANE_KEY,
  TOAST_AUTO_CLOSE_MS,
} from './calendar-v2/constants';
import {
  buildHourWindow,
  buildSessionStats,
  buildSessions,
  buildTutorLanes,
  buildWeekDates,
  filterSessions,
  isHourInPast,
  parseOperatingWindow,
} from './calendar-v2/session-model';
import { fetchReEnrolSessionDates, fetchReEnrolSessions } from './calendar-v2/calendar-api';
import { generateCalendarPdf } from './calendar-v2/print-calendar';
import useCalendarData from './calendar-v2/useCalendarData';
import useCalendarActions from './calendar-v2/useCalendarActions';

import AddEnrolmentModal from './calendar-v2/components/AddEnrolmentModal';
import CalendarToolbar from './calendar-v2/components/CalendarToolbar';
import ConfirmDialog from './calendar-v2/components/ConfirmDialog';
import CreateSessionModal from './calendar-v2/components/CreateSessionModal';
import EditStatusModal from './calendar-v2/components/EditStatusModal';
import FilterPanel from './calendar-v2/components/FilterPanel';
import LegendPanel from './calendar-v2/components/LegendPanel';
import PauseStudentModal from './calendar-v2/components/PauseStudentModal';
import ReEnrolModal from './calendar-v2/components/ReEnrolModal';
import RescheduleSessionModal from './calendar-v2/components/RescheduleSessionModal';
import SessionDrawer from './calendar-v2/components/SessionDrawer';
import StatsStrip from './calendar-v2/components/StatsStrip';
import SwimlaneDayView from './calendar-v2/components/SwimlaneDayView';
import TimelineDayView from './calendar-v2/components/TimelineDayView';
import WeekView from './calendar-v2/components/WeekView';
import RequestsPanel, {
  filterRecordsByLookbackDays,
} from './calendar-v2/components/RequestsPanel';

import './calendar-v2/CalendarV2.css';

const FILTER_REQUEST_COUNT_DAYS = 365;

const DATE_KEY_FORMAT = 'YYYY-MM-DD';
const INBOX_VIEWS = [
  CALENDAR_VIEW.DEFER_REQUESTS,
  CALENDAR_VIEW.DEFER_BIN,
  CALENDAR_VIEW.ONE_OFF_REQUESTS,
  CALENDAR_VIEW.DISAPPROVED,
];

const EMPTY_REENROL_STATE = {
  student: null,
  sessionOptions: [],
  selectedSession: null,
  dateOptions: [],
  selectedDate: '',
};

export default function CalendarV2() {
  const navigate = useNavigate();
  const userSetting = useMemo(() => getUserSetting(), []);
  const today = useMemo(() => Moment().format(DATE_KEY_FORMAT), []);

  /* ---------------- Navigation and display preferences ---------------- */

  const [view, setView] = useState(CALENDAR_VIEW.WEEK);
  const [dayLayout, setDayLayout] = useState(DAY_LAYOUT.TUTOR);
  const [selectedDate, setSelectedDate] = useState(today);
  const [hoursFilter, setHoursFilter] = useState(userSetting.calendarFilterViewMode);
  const [density, setDensity] = useState(DENSITY.DETAILED);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [isRequestsOpen, setIsRequestsOpen] = useState(false);
  /** Active request tab while the requests rail is open. */
  const [openInbox, setOpenInbox] = useState(CALENDAR_VIEW.DEFER_REQUESTS);

  /* ---------------------------- Filters ------------------------------- */

  const [quickFilter, setQuickFilter] = useState(QUICK_FILTER.ALL);
  const [selectedTutorKeys, setSelectedTutorKeys] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  /* ------------------- Drawer, modals and selections ------------------ */

  const [openSessionKey, setOpenSessionKey] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [pauseTarget, setPauseTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [reEnrolState, setReEnrolState] = useState(EMPTY_REENROL_STATE);
  const [assignSelection, setAssignSelection] = useState([]);
  const [isAssignMode, setIsAssignMode] = useState(false);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [createSessionPrefill, setCreateSessionPrefill] = useState(null);
  const [showAddEnrolment, setShowAddEnrolment] = useState(false);

  // Lift Bootstrap modals above the drawer while this page is mounted.
  useEffect(() => {
    document.body.classList.add('cv2-page');
    return () => document.body.classList.remove('cv2-page');
  }, []);

  /* ----------------------------- Data -------------------------------- */

  // One fetch covers the whole week, so switching between day and week — and stepping
  // through days within a week — does not hit the network again.
  const weekDates = useMemo(
    () => buildWeekDates(selectedDate, userSetting.calendarInput.firstDay),
    [selectedDate, userSetting.calendarInput.firstDay]
  );

  const {
    rawSessions,
    operatingHours,
    inboxes,
    notes,
    tutorOptions,
    isInitialLoading,
    isRefreshing,
    sessionsError,
    reloadSessions,
    reloadInboxes,
    reloadNotes,
    markStudentsPaused,
  } = useCalendarData({
    startDate: weekDates[0],
    endDate: weekDates[6],
    hoursFilter,
  });

  const actions = useCalendarActions({
    onSessionsChanged: reloadSessions,
    onInboxesChanged: reloadInboxes,
    onNotesChanged: reloadNotes,
  });

  /* -------------------------- View model ----------------------------- */

  const allSessions = useMemo(
    () => buildSessions(rawSessions, userSetting.locale),
    [rawSessions, userSetting.locale]
  );

  const isWeekView = view === CALENDAR_VIEW.WEEK;

  /** Sessions in the visible period, before the panel's filters. */
  const scopeSessions = useMemo(
    () => (isWeekView ? allSessions : allSessions.filter((session) => session.date === selectedDate)),
    [allSessions, isWeekView, selectedDate]
  );

  const visibleSessions = useMemo(
    () =>
      filterSessions(scopeSessions, {
        tutorKeys: selectedTutorKeys,
        statuses: statusFilter,
        quickFilter,
      }),
    [scopeSessions, selectedTutorKeys, statusFilter, quickFilter]
  );

  // The panel lists every tutor working in the period, so tick boxes do not disappear
  // as soon as a filter is applied.
  const panelLanes = useMemo(() => buildTutorLanes(scopeSessions), [scopeSessions]);
  const dayLanes = useMemo(() => buildTutorLanes(visibleSessions), [visibleSessions]);

  // "All hours" forces the full day (0–24). "Operating hours" pins to centre OPH
  // and widens only for sessions that fall outside them.
  const operatingWindow = useMemo(
    () =>
      String(hoursFilter) === String(HOURS_FILTER.ALL_HOURS)
        ? { allHours: true }
        : parseOperatingWindow(operatingHours, DEFAULT_HOUR_WINDOW),
    [hoursFilter, operatingHours]
  );
  const hourWindow = useMemo(
    () => buildHourWindow(visibleSessions, operatingWindow, DEFAULT_HOUR_WINDOW),
    [visibleSessions, operatingWindow]
  );

  const stats = useMemo(() => buildSessionStats(visibleSessions), [visibleSessions]);

  const inboxCounts = useMemo(
    () => ({
      deferRequests: filterRecordsByLookbackDays(
        inboxes.deferRequests,
        FILTER_REQUEST_COUNT_DAYS
      ).length,
      deferBin: filterRecordsByLookbackDays(inboxes.deferBin, FILTER_REQUEST_COUNT_DAYS)
        .length,
      oneOffRequests: filterRecordsByLookbackDays(
        inboxes.oneOffRequests,
        FILTER_REQUEST_COUNT_DAYS
      ).length,
      disapproved: filterRecordsByLookbackDays(
        inboxes.disapproved,
        FILTER_REQUEST_COUNT_DAYS
      ).length,
    }),
    [inboxes]
  );

  // Resolved from the freshly built list rather than stored, so the drawer reflects the
  // latest data after any refetch.
  const openSession = useMemo(
    () => allSessions.find((session) => String(session.key) === String(openSessionKey)) || null,
    [allSessions, openSessionKey]
  );

  /* ------------------------ Navigation handlers ----------------------- */

  const stepDate = useCallback(
    (direction) => {
      const amount = isWeekView ? 7 : 1;
      setSelectedDate(
        Moment(selectedDate, DATE_KEY_FORMAT).add(direction * amount, 'days').format(DATE_KEY_FORMAT)
      );
    },
    [isWeekView, selectedDate]
  );

  const dateLabel = useMemo(() => {
    if (isWeekView) {
      const start = Moment(weekDates[0], DATE_KEY_FORMAT);
      const end = Moment(weekDates[6], DATE_KEY_FORMAT);
      return `${start.format('DD MMM')} – ${end.format('DD MMM YYYY')}`;
    }
    return Moment(selectedDate, DATE_KEY_FORMAT).format('ddd DD MMM YYYY');
  }, [isWeekView, selectedDate, weekDates]);

  /* -------------------------- Filter handlers ------------------------- */

  const toggleTutor = useCallback(
    (tutorKey) => {
      setSelectedTutorKeys((previous) => {
        const allKeys = panelLanes.map((lane) => lane.tutorKey);
        // An empty list means "all tutors", so the first untick starts from the full set.
        const current = previous.length === 0 ? allKeys : previous;
        const next = current.includes(tutorKey)
          ? current.filter((key) => key !== tutorKey)
          : [...current, tutorKey];

        // Hiding every tutor would leave a grid with no way back, and an empty list
        // already means "all", so both extremes collapse to showing everyone.
        return next.length === 0 || next.length === allKeys.length ? [] : next;
      });
    },
    [panelLanes]
  );

  const toggleStatus = useCallback((status) => {
    setStatusFilter((previous) =>
      previous.includes(status)
        ? previous.filter((entry) => entry !== status)
        : [...previous, status]
    );
  }, []);

  /* ------------------------- Student actions ------------------------- */

  const closeDrawer = useCallback(() => setOpenSessionKey(null), []);

  const handleStatusClick = useCallback((session, student) => {
    setStatusTarget({ session, student });
  }, []);

  const handleModeClick = useCallback(
    (session, student) => {
      actions.toggleStudentMode(student);
    },
    [actions]
  );

  const handleStudentProfileClick = useCallback(
    (student) => {
      // Diagnostics leads (not yet converted) open the Initial Diagnostics list.
      if (student?.isDiagnosticsStudent && !student?.canNavigateToProfile) {
        navigate('/Diagnostics');
        return;
      }
      if (!student?.canNavigateToProfile || !student.studentKey) {
        toast.error('Sorry! Allow Only active Student.', {
          autoClose: TOAST_AUTO_CLOSE_MS,
        });
        return;
      }
      navigate('/view-student', {
        state: { pk_student_key: `${student.studentKey}` },
      });
    },
    [navigate]
  );

  const handleTutorProfileClick = useCallback(
    (sessionOrTutor) => {
      if (sessionOrTutor?.isDiagnostics) {
        navigate('/Diagnostics');
        return;
      }
      const tutorKey = sessionOrTutor?.tutorKey || sessionOrTutor?.fk_tut_key;
      if (!tutorKey || tutorKey === DIAGNOSTICS_LANE_KEY) {
        return;
      }
      navigate('/edit-tutor', {
        state: { pk_tut_key: `${tutorKey}` },
      });
    },
    [navigate]
  );

  const handleSaveStatus = useCallback(
    async (status) => {
      const target = statusTarget;
      setStatusTarget(null);
      if (target) {
        await actions.saveAttendanceStatus(target.student, status);
      }
    },
    [actions, statusTarget]
  );

  const handleDefer = useCallback(
    (session, student) => {
      setConfirmState({
        title: 'Defer this lesson?',
        message: `${student.name} will be added to deferred bin and can be assigned to another session.`,
        confirmLabel: 'Defer lesson',
        onConfirm: () => actions.deferStudent(student),
      });
    },
    [actions]
  );

  const handleOneOff = useCallback(
    (session, student) => actions.requestOneOff(student),
    [actions]
  );

  const handleToggleFlexi = useCallback(
    (session, student) => actions.toggleFlexi(session, student),
    [actions]
  );

  const handlePause = useCallback((session, student) => {
    setPauseTarget({ session, student });
  }, []);

  const handleSavePause = useCallback(
    async (pauseForm) => {
      if (!pauseTarget) {
        return;
      }
      const { session, student } = pauseTarget;
      const succeeded = await actions.pauseStudent(student, session, pauseForm);
      if (succeeded) {
        markStudentsPaused({
          enrolKey: student.raw?.pk_enrol_key,
          studentKey: student.raw?.fk_stu_key || student.studentKey,
          sessionTemplateKey: student.raw?.fk_sest_key || session.sessionTemplateKey,
          sessionDate: session.date,
          applyAll: Number(pauseForm.pauseApplyType) === PAUSE_APPLY_TYPE.ALL,
        });
        setPauseTarget(null);
      }
    },
    [actions, pauseTarget, markStudentsPaused]
  );

  const handleReEnrol = useCallback(async (session, student) => {
    try {
      const sessionOptions = await fetchReEnrolSessions({
        studentKey: student.raw.fk_stu_key,
        reEnrolDate: student.raw.sdate,
        sessionTemplateKey: student.raw.fk_sest_key,
      });

      setReEnrolState({
        ...EMPTY_REENROL_STATE,
        student: { ...student, currentSessionName: session.templateName },
        sessionOptions,
      });
    } catch (error) {
      console.error('Calendar: unable to load re-enrol options', error);
      toast.error('Failure! Unable to load re-enrol options. Try again later.', {
        autoClose: TOAST_AUTO_CLOSE_MS,
      });
    }
  }, []);

  const handleSelectReEnrolSession = useCallback(async (targetSession) => {
    setReEnrolState((previous) => ({ ...previous, selectedSession: targetSession }));

    try {
      const dateOptions = await fetchReEnrolSessionDates(targetSession.pk_sest_key);
      if (dateOptions.length === 0) {
        toast.error('Sorry, this session has no upcoming dates.', {
          autoClose: TOAST_AUTO_CLOSE_MS,
        });
      }
      setReEnrolState((previous) => ({ ...previous, dateOptions }));
    } catch (error) {
      console.error('Calendar: unable to load re-enrol dates', error);
      toast.error('Failure! Unable to load session dates. Try again later.', {
        autoClose: TOAST_AUTO_CLOSE_MS,
      });
    }
  }, []);

  const handleConfirmReEnrol = useCallback(async () => {
    const { student, selectedSession, selectedDate: reEnrolDate } = reEnrolState;
    const succeeded = await actions.reEnrolStudent(student, selectedSession, reEnrolDate);
    if (succeeded) {
      setReEnrolState(EMPTY_REENROL_STATE);
      closeDrawer();
    }
  }, [actions, reEnrolState, closeDrawer]);

  /* -------------------------- Session actions ------------------------ */

  const handleEditSession = useCallback((session) => setRescheduleTarget(session), []);

  const handleSaveReschedule = useCallback(
    async (values) => {
      const target = rescheduleTarget;
      const succeeded = await actions.rescheduleSession(target, values);
      if (succeeded) {
        setRescheduleTarget(null);
        closeDrawer();
      }
    },
    [actions, rescheduleTarget, closeDrawer]
  );

  const openCreateSession = useCallback((prefill = null) => {
    setCreateSessionPrefill(prefill);
    setShowCreateSession(true);
  }, []);

  const handleCreateInSlot = useCallback(
    ({ tutorKey, startHour }) => {
      if (isHourInPast(selectedDate, startHour)) {
        return;
      }
      openCreateSession({
        tutorKey,
        date: selectedDate,
        startHour,
      });
    },
    [openCreateSession, selectedDate]
  );

  const handleSessionCreated = useCallback(() => {
    setShowCreateSession(false);
    setCreateSessionPrefill(null);
    toast.success('Success! Your form was submitted', { autoClose: TOAST_AUTO_CLOSE_MS });
    reloadSessions();
  }, [reloadSessions]);

  const handleEnrolmentCreated = useCallback(() => {
    setShowAddEnrolment(false);
    toast.success('Success! Your form was submitted', { autoClose: TOAST_AUTO_CLOSE_MS });
    reloadSessions();
  }, [reloadSessions]);

  /* --------------------------- Inbox actions ------------------------- */

  const closeRequestsPanel = useCallback(() => setIsRequestsOpen(false), []);

  const toggleFiltersPanel = useCallback(() => {
    setIsPanelOpen((previous) => {
      const next = !previous;
      if (next) {
        setIsRequestsOpen(false);
      }
      return next;
    });
  }, []);

  const toggleRequestsPanel = useCallback(() => {
    setIsRequestsOpen((previous) => {
      const next = !previous;
      if (next) {
        setIsPanelOpen(false);
        setOpenSessionKey(null);
        setOpenInbox((inbox) => inbox || CALENDAR_VIEW.DEFER_REQUESTS);
      }
      return next;
    });
  }, []);

  const toggleAssignSelection = useCallback((record) => {
    setAssignSelection((previous) =>
      previous.some((entry) => entry.cancel_session_key === record.cancel_session_key)
        ? previous.filter((entry) => entry.cancel_session_key !== record.cancel_session_key)
        : [...previous, record]
    );
  }, []);

  const startAssignMode = useCallback(() => {
    setIsAssignMode(true);
  }, []);

  const handleSelectSlot = useCallback(
    (record) => {
      navigate('/ListSlot', {
        state: {
          cancel_session_key: `${record.cancel_session_key}`,
          fk_student_key: `${record.fk_student_key}`,
          pk_enrol_key: `${record.pk_enrol_key}`,
          st_first_name: `${record.st_first_name}`,
          st_surname: `${record.st_surname}`,
          subject_name: `${record.subject_name}`,
          fk_ses_key: `${record.fk_ses_key}`,
        },
      });
    },
    [navigate]
  );

  const cancelAssignMode = useCallback(() => {
    setIsAssignMode(false);
    setAssignSelection([]);
  }, []);

  const handleApproveDefer = useCallback(
    (record) => {
      setConfirmState({
        title: 'Approve this defer request?',
        message:
          'The request will be moved to Deferred Bin',
        confirmLabel: 'Approve',
        onConfirm: () => actions.approveDeferRequest(record),
      });
    },
    [actions]
  );

  const handleDisapproveDefer = useCallback(
    (record) => {
      setConfirmState({
        title: 'Disapprove this defer request?',
        message: 'The lesson will stay on the calendar and the request will be rejected.',
        confirmLabel: 'Disapprove',
        onConfirm: () => actions.disapproveDeferRequest(record),
      });
    },
    [actions]
  );

  const handleSettleDeferred = useCallback(
    (record, forfeitType) => {
      const isForfeit = forfeitType === FORFEIT_TYPE.FORFEIT;
      setConfirmState({
        title: isForfeit ? 'Forfeit this lesson?' : 'Write off this lesson?',
        message: isForfeit
          ? 'The student loses this lesson and it will not be refunded.'
          : 'The lesson will be written off and removed from the deferred bin.',
        confirmLabel: isForfeit ? 'Forfeit' : 'Write off',
        onConfirm: () => actions.settleDeferredSession(record, forfeitType),
      });
    },
    [actions]
  );

  const handleVoidDeferment = useCallback(
    (record) => {
      setConfirmState({
        title: 'Void this deferment?',
        message: 'The lesson returns to the student’s balance and leaves the deferred bin.',
        confirmLabel: 'Void deferment',
        onConfirm: () => actions.voidDeferment(record),
      });
    },
    [actions]
  );

  /** In assign mode, opening a card assigns the selected rows instead of showing detail. */
  const handleOpenSession = useCallback(
    (session) => {
      if (!isAssignMode) {
        setOpenInbox(null);
        setOpenSessionKey(session.key);
        return;
      }

      setConfirmState({
        title: 'Assign to this session?',
        message: `${assignSelection.length} lesson${
          assignSelection.length === 1 ? '' : 's'
        } will be added to ${session.tutorName}'s ${session.timeRangeLabel} session.`,
        confirmLabel: 'Assign',
        onConfirm: async () => {
          const succeeded = await actions.assignSessionsToSlot(session, assignSelection);
          if (succeeded) {
            cancelAssignMode();
          }
          return succeeded;
        },
      });
    },
    [isAssignMode, assignSelection, actions, cancelAssignMode]
  );

  const handleConfirm = useCallback(async () => {
    const pending = confirmState;
    setConfirmState(null);
    if (pending) {
      await pending.onConfirm();
    }
  }, [confirmState]);

  /* ----------------------------- Rendering --------------------------- */

  const selectedAssignKeys = assignSelection.map((record) => record.cancel_session_key);

  function renderGrid() {
    if (isInitialLoading) {
      return <div className="cv2-loading">Loading the calendar…</div>;
    }

    if (sessionsError) {
      return (
        <div className="cv2-empty">
          <span className="cv2-empty__title">Something went wrong</span>
          {sessionsError}
        </div>
      );
    }

    if (visibleSessions.length === 0) {
      return (
        <div className="cv2-empty">
          <span className="cv2-empty__title">No sessions to show</span>
          {scopeSessions.length > 0
            ? 'Every session in this period is hidden by the current filters.'
            : 'Nothing is scheduled for this period.'}
        </div>
      );
    }

    const gridClassName = isRefreshing ? 'cv2-is-refreshing' : undefined;

    return (
      <div className={gridClassName}>
        {isWeekView ? (
          <WeekView
            weekDates={weekDates}
            sessions={visibleSessions}
            hourWindow={hourWindow}
            showFullDay={String(hoursFilter) === String(HOURS_FILTER.ALL_HOURS)}
            density={density}
            todayDate={today}
            onOpenSession={handleOpenSession}
            onStatusClick={handleStatusClick}
            onModeClick={handleModeClick}
            onStudentClick={handleStudentProfileClick}
            onTutorClick={handleTutorProfileClick}
          />
        ) : dayLayout === DAY_LAYOUT.TIMELINE ? (
          <TimelineDayView
            sessions={visibleSessions}
            hourWindow={hourWindow}
            selectedDate={selectedDate}
            density={density}
            onOpenSession={handleOpenSession}
            onStatusClick={handleStatusClick}
            onModeClick={handleModeClick}
            onStudentClick={handleStudentProfileClick}
            onTutorClick={handleTutorProfileClick}
          />
        ) : (
          <SwimlaneDayView
            lanes={dayLanes}
            hourWindow={hourWindow}
            selectedDate={selectedDate}
            density={density}
            onOpenSession={handleOpenSession}
            onStatusClick={handleStatusClick}
            onModeClick={handleModeClick}
            onCreateInSlot={handleCreateInSlot}
            onStudentClick={handleStudentProfileClick}
            onTutorClick={handleTutorProfileClick}
          />
        )}
      </div>
    );
  }

  const isRightRailOpen = isPanelOpen || isRequestsOpen;
  const requestsTotalCount = useMemo(
    () =>
      (inboxCounts.deferRequests || 0) +
      (inboxCounts.deferBin || 0) +
      (inboxCounts.oneOffRequests || 0) +
      (inboxCounts.disapproved || 0),
    [inboxCounts]
  );

  return (
    <div className="cv2-root">
      <StatsStrip stats={stats} centreName={sessionStorage.getItem('center_name') || ''} />

      <div className={`cv2-body${isRightRailOpen ? '' : ' cv2-body--panel-hidden'}`}>
        <main className="cv2-content">
          <div className="cv2-crumb">Calendar</div>
          <div className="cv2-title-row">
            <h1 className="cv2-title">
              {isWeekView ? 'Week schedule' : 'Day schedule'}
              <span className="cv2-title__meta">
                {stats.sessionCount} session{stats.sessionCount === 1 ? '' : 's'} ·{' '}
                {stats.lessonCount} Student{stats.lessonCount === 1 ? '' : 's'}
              </span>
            </h1>
            <div className="cv2-title-row__actions">
              {!isWeekView ? (
                <div className="cv2-segment cv2-day-layout" role="group" aria-label="Day layout">
                  <button
                    type="button"
                    className={`cv2-segment__btn${
                      dayLayout === DAY_LAYOUT.TUTOR ? ' is-active' : ''
                    }`}
                    aria-pressed={dayLayout === DAY_LAYOUT.TUTOR}
                    onClick={() => setDayLayout(DAY_LAYOUT.TUTOR)}
                  >
                    Tutor-wise
                  </button>
                  <button
                    type="button"
                    className={`cv2-segment__btn${
                      dayLayout === DAY_LAYOUT.TIMELINE ? ' is-active' : ''
                    }`}
                    aria-pressed={dayLayout === DAY_LAYOUT.TIMELINE}
                    onClick={() => setDayLayout(DAY_LAYOUT.TIMELINE)}
                  >
                    Timeline
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                className="cv2-segment__btn is-active"
                onClick={() =>
                  generateCalendarPdf({
                    records: rawSessions,
                    view: isWeekView ? CALENDAR_VIEW.WEEK : CALENDAR_VIEW.DAY,
                    hoursFilter,
                    startDate: isWeekView ? weekDates[0] : selectedDate,
                    endDate: isWeekView ? weekDates[6] : selectedDate,
                    locale: userSetting.locale,
                  })
                }
              >
                Print
              </button>
            </div>
          </div>

          <CalendarToolbar
            view={view}
            onViewChange={setView}
            hoursFilter={hoursFilter}
            onHoursFilterChange={setHoursFilter}
            density={density}
            onDensityChange={setDensity}
            dateLabel={dateLabel}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onPrevious={() => stepDate(-1)}
            onNext={() => stepDate(1)}
            onToday={() => setSelectedDate(today)}
            onRefresh={reloadSessions}
            isRefreshing={isRefreshing}
            onAddEnrolment={() => setShowAddEnrolment(true)}
            onCreateSession={() => openCreateSession(null)}
          />

          {isAssignMode ? (
            <div className="cv2-assign-banner">
              <strong>
                {assignSelection.length} lesson
                {assignSelection.length === 1 ? '' : 's'} selected.
              </strong>
              Choose the session that should receive them.
              <button type="button" className="cv2-btn" onClick={cancelAssignMode}>
                Cancel
              </button>
            </div>
          ) : null}

          {renderGrid()}
          <LegendPanel />
        </main>

        <button
          type="button"
          className={`cv2-panel-toggle cv2-panel-toggle--filters${
            isPanelOpen ? ' is-active' : ''
          }`}
          onClick={toggleFiltersPanel}
          aria-label={isPanelOpen ? 'Hide filters' : 'Show filters'}
          aria-pressed={isPanelOpen}
        >
          {isRightRailOpen && isPanelOpen ? (
            <ChevronRightIcon className="cv2-icon" />
          ) : (
            <FilterAltOutlinedIcon className="cv2-icon" style={{ fontSize: 16 }} />
          )}
        </button>

        <button
          type="button"
          className={`cv2-panel-toggle cv2-panel-toggle--requests${
            isRequestsOpen ? ' is-active' : ''
          }`}
          onClick={toggleRequestsPanel}
          aria-label={isRequestsOpen ? 'Hide requests' : 'Show requests'}
          aria-pressed={isRequestsOpen}
        >
          {isRightRailOpen && isRequestsOpen ? (
            <ChevronRightIcon className="cv2-icon" />
          ) : (
            <InboxOutlinedIcon className="cv2-icon" style={{ fontSize: 16 }} />
          )}
          {requestsTotalCount > 0 && !isRequestsOpen ? (
            <span className="cv2-panel-toggle__badge">
              {requestsTotalCount > 99 ? '99+' : requestsTotalCount}
            </span>
          ) : null}
        </button>

        {isPanelOpen ? (
          <FilterPanel
            lanes={panelLanes}
            selectedTutorKeys={selectedTutorKeys}
            onToggleTutor={toggleTutor}
            onSelectAllTutors={() => setSelectedTutorKeys([])}
            onTutorClick={handleTutorProfileClick}
            statusFilter={statusFilter}
            onToggleStatus={toggleStatus}
            notes={notes}
            onAddNote={actions.addNote}
            onDeleteNote={actions.removeNote}
            isNoteSubmitting={actions.isBusy}
            requestCounts={inboxCounts}
            onOpenRequest={(inboxId) => {
              setOpenInbox(inboxId);
              setIsPanelOpen(false);
              setIsRequestsOpen(true);
              setOpenSessionKey(null);
            }}
          />
        ) : null}

        {isRequestsOpen ? (
          <RequestsPanel
            inbox={INBOX_VIEWS.includes(openInbox) ? openInbox : CALENDAR_VIEW.DEFER_REQUESTS}
            inboxes={inboxes}
            busyKey={actions.busyKey}
            isBusy={actions.isBusy}
            selectedKeys={selectedAssignKeys}
            onToggleSelect={toggleAssignSelection}
            onStartAssign={startAssignMode}
            onSelectSlot={handleSelectSlot}
            onApproveDefer={handleApproveDefer}
            onDisapproveDefer={handleDisapproveDefer}
            onSettleDeferred={handleSettleDeferred}
            onVoidDeferment={handleVoidDeferment}
            onSelectInbox={setOpenInbox}
            onClose={closeRequestsPanel}
          />
        ) : null}
      </div>

      {openSession ? (
        <SessionDrawer
          session={openSession}
          busyActionKey={actions.busyKey}
          onClose={closeDrawer}
          onEditStatus={handleStatusClick}
          onToggleMode={handleModeClick}
          onDefer={handleDefer}
          onOneOff={handleOneOff}
          onToggleFlexi={handleToggleFlexi}
          onReEnrol={handleReEnrol}
          onPause={handlePause}
          onEditSession={handleEditSession}
          onStudentClick={handleStudentProfileClick}
          onTutorClick={handleTutorProfileClick}
        />
      ) : null}

      <EditStatusModal
        show={Boolean(statusTarget)}
        student={statusTarget ? statusTarget.student : null}
        isSubmitting={actions.isBusy}
        onCancel={() => setStatusTarget(null)}
        onSave={handleSaveStatus}
      />

      <PauseStudentModal
        show={Boolean(pauseTarget)}
        student={pauseTarget ? pauseTarget.student : null}
        sessionTemplateName={pauseTarget ? pauseTarget.session.templateName || '' : ''}
        isSubmitting={actions.isBusy}
        onCancel={() => setPauseTarget(null)}
        onSave={handleSavePause}
      />

      <RescheduleSessionModal
        show={Boolean(rescheduleTarget)}
        session={rescheduleTarget}
        tutorOptions={tutorOptions}
        isSubmitting={actions.isBusy}
        onCancel={() => setRescheduleTarget(null)}
        onSave={handleSaveReschedule}
      />

      <CreateSessionModal
        show={showCreateSession}
        tutorOptions={tutorOptions}
        prefill={createSessionPrefill}
        onCancel={() => {
          setShowCreateSession(false);
          setCreateSessionPrefill(null);
        }}
        onCreated={handleSessionCreated}
      />

      <AddEnrolmentModal
        show={showAddEnrolment}
        onCancel={() => setShowAddEnrolment(false)}
        onCreated={handleEnrolmentCreated}
      />

      <ReEnrolModal
        show={Boolean(reEnrolState.student)}
        student={reEnrolState.student}
        sessionOptions={reEnrolState.sessionOptions}
        selectedSession={reEnrolState.selectedSession}
        dateOptions={reEnrolState.dateOptions}
        selectedDate={reEnrolState.selectedDate}
        isSubmitting={actions.isBusy}
        onSelectSession={handleSelectReEnrolSession}
        onSelectDate={(date) =>
          setReEnrolState((previous) => ({ ...previous, selectedDate: date }))
        }
        onCancel={() => setReEnrolState(EMPTY_REENROL_STATE)}
        onConfirm={handleConfirmReEnrol}
      />

      <ConfirmDialog
        show={Boolean(confirmState)}
        title={confirmState ? confirmState.title : ''}
        message={confirmState ? confirmState.message : ''}
        confirmLabel={confirmState ? confirmState.confirmLabel : 'Confirm'}
        isSubmitting={actions.isBusy}
        onCancel={() => setConfirmState(null)}
        onConfirm={handleConfirm}
      />

      <ToastContainer />
    </div>
  );
}
